import type { JSX } from "react";
import { addCalendarDays, type Day } from "@recipevault/shared";

interface WeekStripProps {
  weekStart: string;
  selectedDate: string;
  today: string;
  /** The week's days, for per-day markers. May be sparse (a date not present here
   * is treated as an empty, unplanned day — never crashes, never fakes a marker). */
  days: Day[];
  onSelect: (date: string) => void;
  onWeekChange: (newWeekStart: string) => void;
}

const WEEKDAY_LETTER_FORMATTER = new Intl.DateTimeFormat("en-US", {
  weekday: "narrow",
  timeZone: "UTC",
});

/** Single narrow weekday letter for an ISO calendar date, anchored at UTC noon (no DST
 * edge) — mirrors `DaySection.tsx`'s `weekdayLabel` pattern. Never `new Date(isoDate)`
 * directly: that parses as local midnight and can shift the displayed day. */
function weekdayLetter(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map((v) => Number.parseInt(v, 10)) as [number, number, number];
  return WEEKDAY_LETTER_FORMATTER.format(new Date(Date.UTC(y, m - 1, d, 12)));
}

/** The `DD` component of an ISO calendar date, taken directly from the string — no
 * Date round-trip needed for a plain substring. */
function dayOfMonth(isoDate: string): string {
  return isoDate.split("-")[2] ?? "";
}

type MarkerState = "check" | "dot" | "none";

/**
 * Honest per-day marker (spec-resolved rule, see task brief). A slot only counts toward
 * "needs cooking" if it has real items — a leftover slot is non-empty (it counts toward
 * "planned") but can never earn a `cookedAt` (you don't cook leftovers), so it must never
 * block the day's checkmark. Example: a cooked dinner + a leftover lunch is a fully-done
 * day (✓), not a "still pending" day (dot) — the leftover doesn't count against it.
 */
function markerStateFor(day: Day | undefined): MarkerState {
  if (!day) return "none";
  const cookSlots = day.slots.filter((s) => s.skipped !== true && s.items.length > 0);
  const plannedSlots = day.slots.filter(
    (s) => s.skipped !== true && (s.items.length > 0 || s.leftoverOf !== undefined),
  );
  const showCheck = cookSlots.length > 0 && cookSlots.every((s) => s.cookedAt != null);
  const showDot = plannedSlots.length > 0 && !showCheck;
  if (showCheck) return "check";
  if (showDot) return "dot";
  return "none";
}

const CHIP_BASE =
  "flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg border px-1 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50";

const CHEVRON_BASE =
  "flex min-h-12 min-w-12 items-center justify-center rounded-md border border-zinc-800 text-lg text-zinc-400 transition-colors hover:border-zinc-700 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50";

/**
 * The only remaining at-a-glance week view (Plan 14 day-pager). Seven equal-width day
 * chips between two chevrons — chevrons page `weekStart` by ±7 calendar days (handled by
 * the parent route), chip tap selects a day, and each chip's marker slot honestly reports
 * that day's cook state: nothing for a truly empty day, a dot for "planned, not fully
 * cooked", a ✓ for "every cook-slot cooked" (see `markerStateFor`).
 */
export function WeekStrip({
  weekStart,
  selectedDate,
  today,
  days,
  onSelect,
  onWeekChange,
}: WeekStripProps): JSX.Element {
  const dates = Array.from({ length: 7 }, (_, i) => addCalendarDays(weekStart, i));

  return (
    <div className="flex items-stretch gap-1" role="group" aria-label="Week">
      <button
        type="button"
        aria-label="Previous week"
        onClick={() => {
          onWeekChange(addCalendarDays(weekStart, -7));
        }}
        className={CHEVRON_BASE}
      >
        <span aria-hidden>‹</span>
      </button>

      {dates.map((date) => {
        const day = days.find((d) => d.date === date);
        const isSelected = date === selectedDate;
        const isToday = date === today;
        const marker = markerStateFor(day);

        return (
          <button
            key={date}
            type="button"
            aria-label={date}
            aria-pressed={isSelected}
            data-testid={`week-strip-chip-${date}`}
            onClick={() => {
              onSelect(date);
            }}
            className={`${CHIP_BASE} ${
              isSelected
                ? "border-emerald-500 bg-emerald-500/10"
                : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
            }`}
          >
            <span className="flex items-center gap-1">
              <span
                className={`text-[11px] font-medium uppercase tracking-wide ${
                  isToday ? "text-emerald-400" : "text-zinc-500"
                }`}
              >
                {weekdayLetter(date)}
              </span>
              {isToday && (
                <span
                  data-testid={`week-strip-today-dot-${date}`}
                  aria-hidden
                  className="h-1 w-1 rounded-full bg-emerald-400"
                />
              )}
            </span>
            <span
              className={`text-sm font-semibold ${isToday ? "text-emerald-400" : "text-zinc-200"}`}
            >
              {dayOfMonth(date)}
            </span>
            <span className="flex h-2 items-center justify-center" aria-hidden>
              {marker === "check" && (
                <span
                  data-testid={`week-strip-check-${date}`}
                  className="text-[10px] font-bold leading-none text-emerald-400"
                >
                  ✓
                </span>
              )}
              {marker === "dot" && (
                <span
                  data-testid={`week-strip-dot-${date}`}
                  className="h-1.5 w-1.5 rounded-full bg-emerald-500/50"
                />
              )}
            </span>
          </button>
        );
      })}

      <button
        type="button"
        aria-label="Next week"
        onClick={() => {
          onWeekChange(addCalendarDays(weekStart, 7));
        }}
        className={CHEVRON_BASE}
      >
        <span aria-hidden>›</span>
      </button>
    </div>
  );
}
